import React, { useState } from 'react';
import { X, Plus, Trash2, Monitor } from 'lucide-react';
import { CustomDisplayConfig } from './hooks/useCustomDisplays';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';

interface CustomDisplayDialogProps {
    isOpen: boolean;
    onClose: () => void;
    customDisplays: any[];
    onAdd: (config: CustomDisplayConfig) => void;
    onDelete: (id: string) => void;
}

export default function CustomDisplayDialog({
    isOpen,
    onClose,
    customDisplays,
    onAdd,
    onDelete,
}: CustomDisplayDialogProps) {
    const [name, setName] = useState('');
    const [pixelWidth, setPixelWidth] = useState(1920);
    const [pixelHeight, setPixelHeight] = useState(1080);
    const [scale, setScale] = useState(1.5);
    const [showForm, setShowForm] = useState(false);

    // Calculate points based on pixels and scale
    const pointWidth = Math.round(pixelWidth / scale);
    const pointHeight = Math.round(pixelHeight / scale);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            alert('Please enter a display name');
            return;
        }

        const config: CustomDisplayConfig = {
            id: `custom-display-${Date.now()}`,
            name: name.trim(),
            pixels: {
                width: pixelWidth,
                height: pixelHeight,
            },
            scale,
            points: {
                width: pointWidth,
                height: pointHeight,
            },
        };

        onAdd(config);

        // Reset form
        setName('');
        setPixelWidth(1920);
        setPixelHeight(1080);
        setScale(1.5);
        setShowForm(false);
    };

    const handleCancel = () => {
        setName('');
        setPixelWidth(1920);
        setPixelHeight(1080);
        setScale(1.5);
        setShowForm(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-background rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">
                        Custom Displays
                    </h2>
                    <Button variant="ghost" size="icon" onClick={onClose}>
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {/* Existing Custom Displays */}
                    {customDisplays.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-foreground mb-3">
                                Your Custom Displays
                            </h3>
                            <div className="space-y-2">
                                {customDisplays.map((display) => (
                                    <div
                                        key={display.id}
                                        className="flex items-center justify-between p-3 border border-border rounded-md bg-muted/50"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Monitor className="w-5 h-5 text-muted-foreground" />
                                            <div>
                                                <div className="font-medium text-sm text-foreground">
                                                    {display.name}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {display.pixels.width}×
                                                    {display.pixels.height}px @
                                                    {display.scale}× →{' '}
                                                    {display.points.width}×
                                                    {display.points.height}pt
                                                </div>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => onDelete(display.id)}
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            title="Delete display"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Add New Display */}
                    {!showForm ? (
                        <Button onClick={() => setShowForm(true)}>
                            <Plus className="w-4 h-4" />
                            Add Custom Display
                        </Button>
                    ) : (
                        <div className="border border-border rounded-lg p-4 bg-background">
                            <h3 className="text-sm font-medium text-foreground mb-4">
                                New Custom Display
                            </h3>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Display Name */}
                                <div>
                                    <label className="block text-xs font-medium text-foreground mb-1">
                                        Display Name
                                    </label>
                                    <Input
                                        type="text"
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        placeholder="e.g., 4K Display, Custom Monitor"
                                        required
                                    />
                                </div>

                                {/* Pixel Dimensions */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-foreground mb-1">
                                            Width (pixels)
                                        </label>
                                        <Input
                                            type="number"
                                            value={pixelWidth}
                                            onChange={(e) =>
                                                setPixelWidth(
                                                    parseInt(e.target.value) ||
                                                        0,
                                                )
                                            }
                                            min="1"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-foreground mb-1">
                                            Height (pixels)
                                        </label>
                                        <Input
                                            type="number"
                                            value={pixelHeight}
                                            onChange={(e) =>
                                                setPixelHeight(
                                                    parseInt(e.target.value) ||
                                                        0,
                                                )
                                            }
                                            min="1"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Pixel Density */}
                                <div>
                                    <label className="block text-xs font-medium text-foreground mb-1">
                                        Pixel Density (Scale)
                                    </label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="number"
                                            step="0.1"
                                            value={scale}
                                            onChange={(e) =>
                                                setScale(
                                                    parseFloat(
                                                        e.target.value,
                                                    ) || 1,
                                                )
                                            }
                                            min="0.5"
                                            max="4"
                                            className="flex-1"
                                            required
                                        />
                                        <div className="flex gap-1">
                                            <Button
                                                type="button"
                                                variant={scale === 1 ? 'default' : 'outline'}
                                                size="xs"
                                                onClick={() => setScale(1)}
                                            >
                                                1×
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={scale === 1.5 ? 'default' : 'outline'}
                                                size="xs"
                                                onClick={() => setScale(1.5)}
                                            >
                                                1.5×
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={scale === 2 ? 'default' : 'outline'}
                                                size="xs"
                                                onClick={() => setScale(2)}
                                            >
                                                2×
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={scale === 3 ? 'default' : 'outline'}
                                                size="xs"
                                                onClick={() => setScale(3)}
                                            >
                                                3×
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="mt-1 text-xs text-muted-foreground">
                                        Common: 1× (standard), 1.5× (high-res),
                                        2× (Retina), 3× (iPhone)
                                    </div>
                                </div>

                                {/* Calculated Points (Read-only) */}
                                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                                    <div className="text-xs font-medium text-blue-900 mb-1">
                                        Calculated Point Dimensions
                                    </div>
                                    <div className="text-sm text-blue-700 font-mono">
                                        {pointWidth} × {pointHeight} points
                                    </div>
                                    <div className="mt-1 text-xs text-blue-600">
                                        These are the logical dimensions used
                                        for layout
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div className="flex justify-end gap-2 pt-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit">
                                        Add Display
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-border p-4 bg-muted/50">
                    <div className="text-xs text-muted-foreground">
                        <strong>Tip:</strong> Custom displays are saved locally
                        and will be available across all your experiences.
                    </div>
                </div>
            </div>
        </div>
    );
}
